/**
 * StorageLunsPanel — LUNs (unidades lógicas) publicados por una cabina.
 *
 * Cada LUN tiene su protocolo, tamaño, IQN del target y sus **caminos**
 * (portales). Un camino puede apuntar a un puerto físico de la cabina
 * (`component_port_id`), que es el que ya sale cableado como iSCSI-A/iSCSI-B,
 * así que la cadena queda cerrada: volumen del servidor → LUN → portal →
 * puerto → cable.
 *
 * Abajo se ve a qué servidores/VMs está presentado cada LUN (los volúmenes que
 * lo usan), que es lo que el diseñador dibuja como cadena.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  PlusIcon, TrashIcon, PencilSquareIcon, XMarkIcon, LinkIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
const PROTOCOLS = [
  ['iscsi', 'iSCSI'],
  ['fc', 'Fibre Channel'],
  ['nvmeof', 'NVMe-oF'],
  ['nfs', 'NFS'],
  ['smb', 'SMB / CIFS'],
  ['local', 'Local (DAS)'],
  ['other', 'Otro'],
];

const STATUSES = [
  ['online', 'En línea'],
  ['offline', 'Offline'],
  ['degraded', 'Degradado'],
];

const EMPTY_LUN = {
  name: '', lun_id: '', protocol: 'iscsi', size_gb: '', pool: '',
  thin_provisioned: true, target_iqn: '', portals: [], status: 'online',
  is_shared: false, notes: '',
};

const EMPTY_PORTAL = { label: '', address: '', port: '', iqn: '', component_port_id: '', notes: '' };

function fmtGb(value) {
  if (value == null || value === '') return '—';
  const gb = Number(value);
  if (!Number.isFinite(gb)) return '—';
  if (gb >= 1024) return `${(gb / 1024).toLocaleString('es-ES', { maximumFractionDigits: 2 })} TB`;
  return `${gb.toLocaleString('es-ES')} GB`;
}

const th = {
  textAlign: 'left', padding: '5px 8px', color: 'var(--text-muted)',
  borderBottom: '1px solid var(--slate-300)', whiteSpace: 'nowrap', fontSize: '0.72rem',
};
const td = {
  padding: '5px 8px', borderBottom: '1px solid var(--slate-200)',
  color: 'var(--text-primary)', fontSize: '0.8rem', verticalAlign: 'top',
};

export default function StorageLunsPanel({ elementId, companyId, ports = [], onChanged }) {
  const [luns, setLuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // { id?, ...form }
  // Volúmenes ya dados de alta en la empresa, para poder presentarlos desde aquí
  // sin tener que ir ficha por ficha: {id, ci_name, mount_point, label, lun_id…}
  const [volumes, setVolumes] = useState([]);
  const [pickVolume, setPickVolume] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.getLuns(companyId, elementId);
      setLuns(res.data || []);
    } catch (e) {
      setError(e.message || 'No se pudieron cargar los LUNs');
    } finally {
      setLoading(false);
    }
  }, [companyId, elementId]);

  useEffect(() => { if (elementId) load(); }, [elementId, load]);

  /** Volúmenes ya dados de alta (de toda la empresa) para poder presentarlos a
      este LUN sin tener que ir a la ficha de cada servidor. */
  const loadCandidateVolumes = useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await api.getVolumes(companyId);
      setVolumes(res.data || []);
    } catch {
      setVolumes([]);
    }
  }, [companyId]);

  /** Volúmenes que pueden colgar de este LUN: los que no cuelgan de ninguno y
      los de otras cabinas (se marcan, porque al presentarlos se mueven). */
  const candidatesFor = (lun) => (volumes || []).filter(
    v => !lun?.id || v.lun_id !== lun.id
  );

  const volumeLabel = (v) => `${v.ci_name || `${v.ci_type} ${v.ci_id}`} · ${v.mount_point || v.label || 'volumen'}`;

  const linkVolume = async (volumeId) => {
    if (!editing?.id || !volumeId) return;
    setSaving(true);
    setError('');
    try {
      await api.updateVolume(volumeId, { lun_id: editing.id }, companyId);
      setPickVolume('');
      await Promise.all([load(), loadCandidateVolumes()]);
      onChanged?.();
    } catch (e) {
      setError(e.message || 'No se pudo presentar el volumen a este LUN');
    } finally {
      setSaving(false);
    }
  };

  const unlinkVolume = async (volumeId) => {
    setSaving(true);
    setError('');
    try {
      await api.updateVolume(volumeId, { lun_id: null }, companyId);
      await Promise.all([load(), loadCandidateVolumes()]);
      onChanged?.();
    } catch (e) {
      setError(e.message || 'No se pudo quitar el volumen de este LUN');
    } finally {
      setSaving(false);
    }
  };

  const openNew = () => {
    setPickVolume('');
    loadCandidateVolumes();
    setEditing({ ...EMPTY_LUN });
  };

  const openEdit = (lun) => {
    setPickVolume('');
    loadCandidateVolumes();
    setEditing({
      id: lun.id,
      name: lun.name || '',
      lun_id: lun.lun_id ?? '',
      protocol: lun.protocol || 'iscsi',
      size_gb: lun.size_gb ?? '',
      pool: lun.pool || '',
      thin_provisioned: !!lun.thin_provisioned,
      target_iqn: lun.target_iqn || '',
      portals: (lun.portals || []).map(p => ({
        label: p.label || '', address: p.address || '', port: p.port || '',
        iqn: p.iqn || '', component_port_id: p.component_port_id ?? '', notes: p.notes || '',
      })),
      status: lun.status || 'online',
      is_shared: !!lun.is_shared,
      notes: lun.notes || '',
    });
  };

  const save = async () => {
    if (!editing?.name?.trim()) { setError('El LUN necesita un nombre'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...editing,
        element_id: elementId,
        lun_id: editing.lun_id === '' ? null : Number(editing.lun_id),
        size_gb: editing.size_gb === '' ? null : Number(editing.size_gb),
        portals: (editing.portals || []).filter(
          p => p.label || p.address || p.iqn || p.component_port_id
        ).map(p => ({
          ...p,
          component_port_id: p.component_port_id === '' ? null : Number(p.component_port_id),
        })),
      };
      if (editing.id) await api.updateLun(editing.id, payload, companyId);
      else await api.createLun(payload, companyId);
      setEditing(null);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e.message || 'No se pudo guardar el LUN');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (lun) => {
    setError('');
    try {
      await api.deleteLun(lun.id, companyId);
      await load();
      onChanged?.();
    } catch (e) {
      // 409: el LUN está presentado a algún servidor
      const count = lun.presented_count || 0;
      if (window.confirm(
        `${e.message}\n\n¿Quieres borrarlo de todas formas? Los volúmenes quedarán sin LUN de origen.`
      )) {
        try {
          await api.deleteLun(lun.id, companyId, true);
          await load();
          onChanged?.();
        } catch (e2) {
          setError(e2.message || 'No se pudo borrar el LUN');
        }
      } else if (!count) {
        setError(e.message || 'No se pudo borrar el LUN');
      }
    }
  };

  const updatePortal = (idx, patch) => {
    const next = (editing.portals || []).slice();
    next[idx] = { ...next[idx], ...patch };
    setEditing({ ...editing, portals: next });
  };

  const portLabel = (portId) => {
    const p = ports.find(x => String(x.id) === String(portId));
    if (!p) return null;
    return `${p.label || `Puerto ${p.port_number}`}${p.port_type ? ` · ${p.port_type}` : ''}`;
  };

  return (
    <div className="dv-section" style={{
      background: 'rgba(101,163,13,.06)', border: '1px solid rgba(101,163,13,.3)',
      borderRadius: 8, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1, fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)' }}>
          LUNs publicados
          <span style={{
            marginLeft: 6, fontSize: '0.7rem', background: 'var(--bg-input)',
            color: 'var(--text-muted)', borderRadius: 4, padding: '0 6px',
          }}>
            {luns.length}
          </span>
        </div>
        {!editing && (
          <button className="dv-btn" type="button" onClick={openNew}>
            <PlusIcon style={{ width: 14, height: 14 }} /> Añadir LUN
          </button>
        )}
      </div>

      <p className="dv-hint" style={{ marginBottom: 10 }}>
        Un LUN es la unidad que publica la cabina (o un servidor que hace de cabina, con
        iSCSI Target) y que los equipos ven como disco (iSCSI/FC). Su <strong>camino</strong> puede
        apuntar a un puerto físico del equipo publicador (los que ya se cablean como
        iSCSI-A/iSCSI-B) y luego el servidor lo ve como <strong>volumen</strong> en su ficha → pestaña
        <strong> Almacenamiento</strong>. El diseñador de servicios dibuja esa cadena.
      </p>

      {error && (
        <div style={{
          color: '#dc2626', fontSize: '0.78rem', marginBottom: 8,
          background: 'rgba(220,38,38,.08)', border: '1px solid rgba(220,38,38,.3)',
          borderRadius: 6, padding: '6px 8px',
        }}>
          ⚠ {error}
        </div>
      )}

      {/* ── Editor ── */}
      {editing && (
        <div style={{
          border: '1px solid var(--slate-300)', borderRadius: 8, padding: 10,
          background: 'var(--bg-card)', marginBottom: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <strong style={{ flex: 1, fontSize: '0.8rem' }}>
              {editing.id ? `Editar LUN: ${editing.name}` : 'Nuevo LUN'}
            </strong>
            <button className="dv-btn" type="button" onClick={() => setEditing(null)} title="Cerrar">
              <XMarkIcon style={{ width: 14, height: 14 }} />
            </button>
          </div>

          <div className="row g-2">
            <div className="col-md-4">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>Nombre *</label>
              <input className="form-control form-control-sm" placeholder="ej. LUN-01 SQL DATA"
                     value={editing.name}
                     onChange={e => setEditing({ ...editing, name: e.target.value })} />
            </div>
            <div className="col-md-2">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>Nº de LUN</label>
              <input className="form-control form-control-sm" type="number" min="0"
                     placeholder="ej. 0"
                     value={editing.lun_id}
                     onChange={e => setEditing({ ...editing, lun_id: e.target.value })} />
            </div>
            <div className="col-md-2">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>Tamaño (GB)</label>
              <input className="form-control form-control-sm" type="number" min="0"
                     placeholder="ej. 1024"
                     value={editing.size_gb}
                     onChange={e => setEditing({ ...editing, size_gb: e.target.value })} />
            </div>
            <div className="col-md-2">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>Protocolo</label>
              <select className="form-select form-select-sm" value={editing.protocol}
                      onChange={e => setEditing({ ...editing, protocol: e.target.value })}>
                {PROTOCOLS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>Estado</label>
              <select className="form-select form-select-sm" value={editing.status}
                      onChange={e => setEditing({ ...editing, status: e.target.value })}>
                {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>Pool / aggregate de origen</label>
              <input className="form-control form-control-sm" placeholder="ej. pool_flash_01"
                     value={editing.pool}
                     onChange={e => setEditing({ ...editing, pool: e.target.value })} />
            </div>
            <div className="col-md-5">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>IQN del target (en la cabina)</label>
              <input className="form-control form-control-sm" style={{ fontFamily: 'monospace', fontSize: 12 }}
                     placeholder="ej. iqn.2026-01.local.storage01:lun01"
                     value={editing.target_iqn}
                     onChange={e => setEditing({ ...editing, target_iqn: e.target.value })} />
            </div>
            <div className="col-md-3 d-flex align-items-center" style={{ gap: 12, paddingTop: 18 }}>
              <label style={{ fontSize: '0.76rem', display: 'flex', alignItems: 'center', gap: 5 }}>
                <input type="checkbox" checked={editing.thin_provisioned}
                       onChange={e => setEditing({ ...editing, thin_provisioned: e.target.checked })} />
                Thin
              </label>
              <label style={{ fontSize: '0.76rem', display: 'flex', alignItems: 'center', gap: 5 }}>
                <input type="checkbox" checked={editing.is_shared}
                       onChange={e => setEditing({ ...editing, is_shared: e.target.checked })} />
                Compartido (clúster)
              </label>
            </div>
            <div className="col-12">
              <label className="form-label" style={{ fontSize: '0.7rem' }}>Notas</label>
              <input className="form-control form-control-sm"
                     placeholder="ej. volumen de datos del SQL, snapshot diario"
                     value={editing.notes}
                     onChange={e => setEditing({ ...editing, notes: e.target.value })} />
            </div>
          </div>

          {/* ── Caminos / portales ── */}
          <div style={{ marginTop: 10, borderTop: '1px solid var(--slate-200)', paddingTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div style={{ flex: 1, fontSize: '0.74rem', fontWeight: 700 }}>
                Caminos del LUN (iSCSI: un camino por cada red/puerto)
              </div>
              <button className="dv-btn" type="button"
                      onClick={() => setEditing({
                        ...editing,
                        portals: [...(editing.portals || []), { ...EMPTY_PORTAL }],
                      })}>
                <PlusIcon style={{ width: 14, height: 14 }} /> Añadir camino
              </button>
            </div>
            {(editing.portals || []).length === 0 ? (
              <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                Sin caminos documentados. Con multipath iSCSI suele haber dos (iSCSI-A e iSCSI-B).
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(editing.portals || []).map((p, idx) => (
                  <div key={idx} className="row g-2" style={{ alignItems: 'flex-end' }}>
                    <div className="col-md-2">
                      <label className="form-label" style={{ fontSize: '0.68rem' }}>Etiqueta</label>
                      <input className="form-control form-control-sm" placeholder="iSCSI-A"
                             value={p.label}
                             onChange={e => updatePortal(idx, { label: e.target.value })} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label" style={{ fontSize: '0.68rem' }}>IP / portal</label>
                      <input className="form-control form-control-sm" placeholder="10.60.9.11"
                             value={p.address}
                             onChange={e => updatePortal(idx, { address: e.target.value })} />
                    </div>
                    <div className="col-md-1">
                      <label className="form-label" style={{ fontSize: '0.68rem' }}>Puerto</label>
                      <input className="form-control form-control-sm" placeholder="3260"
                             value={p.port}
                             onChange={e => updatePortal(idx, { port: e.target.value })} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label" style={{ fontSize: '0.68rem' }}>IQN del portal</label>
                      <input className="form-control form-control-sm" style={{ fontFamily: 'monospace', fontSize: 11 }}
                             placeholder="iqn.2026-01…:iscsi-a"
                             value={p.iqn}
                             onChange={e => updatePortal(idx, { iqn: e.target.value })} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label" style={{ fontSize: '0.68rem' }}>Puerto físico de la cabina</label>
                      <select className="form-select form-select-sm"
                              value={p.component_port_id ?? ''}
                              onChange={e => updatePortal(idx, { component_port_id: e.target.value })}>
                        <option value="">— Sin vincular —</option>
                        {ports.map(pt => (
                          <option key={pt.id} value={pt.id}>
                            {pt.label || `Puerto ${pt.port_number}`}
                            {pt.port_type ? ` · ${pt.port_type}` : ''}
                            {pt.mac_address ? ` · ${pt.mac_address}` : ''}
                            {(pt.wwn ? ` · ${pt.wwn}` : '')}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-auto">
                      <button className="dv-btn" type="button" title="Quitar camino"
                              onClick={() => setEditing({
                                ...editing,
                                portals: editing.portals.filter((_, i) => i !== idx),
                              })}>
                        <TrashIcon style={{ width: 14, height: 14 }} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Presentado a: volúmenes que montan este LUN ── */}
          <div style={{ marginTop: 10, borderTop: '1px solid var(--slate-200)', paddingTop: 8 }}>
            <div style={{ fontSize: '0.74rem', fontWeight: 700, marginBottom: 4 }}>
              Presentado a (qué equipos montan este LUN)
            </div>
            {!editing.id ? (
              <p className="dv-hint" style={{ margin: 0 }}>
                Guarda el LUN y podrás presentarlo aquí a los volúmenes ya dados de alta.
              </p>
            ) : (
              <>
                <p className="dv-hint" style={{ marginBottom: 6 }}>
                  Un LUN puede presentarse a varios equipos (clúster). Se enlaza con los
                  volúmenes que ya existen en las fichas de servidor / VM; también puedes
                  hacerlo desde la pestaña <strong>Almacenamiento</strong> de cada equipo.
                </p>

                {(volumes.filter(v => v.lun_id === editing.id).length === 0) ? (
                  <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 6 }}>
                    Ningún volumen de este LUN todavía.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 6 }}>
                    {volumes.filter(v => v.lun_id === editing.id).map(v => (
                      <div key={v.id} style={{
                        display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem',
                        background: 'rgba(101,163,13,.08)', border: '1px solid rgba(101,163,13,.25)',
                        borderRadius: 6, padding: '3px 8px',
                      }}>
                        <LinkIcon style={{ width: 12, height: 12, color: '#84cc16', flexShrink: 0 }} />
                        <span style={{ flex: 1, color: 'var(--text-primary)' }}>
                          <strong>{v.ci_name || `${v.ci_type} ${v.ci_id}`}</strong>
                          {' · '}{v.mount_point || v.label || 'volumen'}
                          {v.filesystem ? ` · ${v.filesystem}` : ''}
                          {v.size_gb ? ` · ${fmtGb(v.size_gb)}` : ''}
                        </span>
                        <button className="dv-btn" type="button" disabled={saving}
                                title="Dejar de presentar este LUN a ese volumen"
                                onClick={() => unlinkVolume(v.id)}>
                          <XMarkIcon style={{ width: 13, height: 13 }} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="row g-2">
                  <div className="col-md-8">
                    <label className="form-label" style={{ fontSize: '0.68rem' }}>
                      Presentar a un volumen ya dado de alta
                    </label>
                    <select className="form-select form-select-sm" value={pickVolume}
                            onChange={e => setPickVolume(e.target.value)}>
                      <option value="">— Elegir volumen —</option>
                      {candidatesFor(editing).map(v => (
                        <option key={v.id} value={v.id}>
                          {volumeLabel(v)}
                          {v.filesystem ? ` · ${v.filesystem}` : ''}
                          {v.size_gb ? ` · ${fmtGb(v.size_gb)}` : ''}
                          {v.lun_id ? ' · en otro LUN (se moverá)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-auto d-flex align-items-end">
                    <button className="dv-btn" type="button" disabled={!pickVolume || saving}
                            onClick={() => linkVolume(pickVolume)}>
                      <PlusIcon style={{ width: 14, height: 14 }} /> Presentar
                    </button>
                  </div>
                </div>
                {candidatesFor(editing).length === 0 && (
                  <p className="dv-hint" style={{ marginTop: 4, marginBottom: 0 }}>
                    {(volumes || []).length === 0
                      ? <>No hay volúmenes dados de alta en esta empresa: se crean en la pestaña
                          <strong> Almacenamiento</strong> de la ficha del servidor, VM o puesto.</>
                      : 'Todos los volúmenes de la empresa ya cuelgan de este LUN.'}
                  </p>
                )}
              </>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
            <button className="dv-btn" type="button" onClick={() => setEditing(null)}>Cancelar</button>
            <button className="dv-btn dv-btn-primary" type="button" onClick={save} disabled={saving}>
              {saving ? 'Guardando…' : (editing.id ? 'Guardar cambios' : 'Crear LUN')}
            </button>
          </div>
        </div>
      )}

      {/* ── Lista ── */}
      {loading ? (
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Cargando LUNs…</div>
      ) : luns.length === 0 ? (
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Esta cabina no tiene LUNs documentados todavía.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['LUN', 'Nº', 'Protocolo', 'Tamaño', 'Pool', 'Camino', 'Presentado a', 'Estado', ''].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {luns.map(lun => (
                <tr key={lun.id}>
                  <td style={{ ...td, fontWeight: 600 }}>
                    {lun.name}
                    {lun.is_shared && (
                      <span title="Compartido entre varios hosts" style={{
                        marginLeft: 6, fontSize: '0.62rem', background: 'rgba(139,92,246,.18)',
                        color: '#c4b5fd', borderRadius: 3, padding: '0 5px',
                      }}>
                        compartido
                      </span>
                    )}
                    {lun.target_iqn && (
                      <div style={{ fontFamily: 'monospace', fontSize: '0.66rem', color: 'var(--text-muted)' }}>
                        {lun.target_iqn}
                      </div>
                    )}
                  </td>
                  <td style={td}>{lun.lun_id ?? '—'}</td>
                  <td style={td}>{lun.protocol_label || lun.protocol}</td>
                  <td style={td}>{fmtGb(lun.size_gb)}</td>
                  <td style={{ ...td, color: 'var(--text-muted)' }}>
                    {lun.pool || '—'}
                    {lun.thin_provisioned && <> · thin</>}
                  </td>
                  <td style={td}>
                    {(lun.portals || []).length === 0 ? '—' : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {lun.portals.map((p, i) => (
                          <span key={i} style={{ fontSize: '0.72rem' }}>
                            <strong>{p.label || `Camino ${i + 1}`}</strong>
                            {p.address && <> · {p.address}{p.port ? `:${p.port}` : ''}</>}
                            {portLabel(p.component_port_id) && (
                              <span style={{ color: 'var(--text-muted)' }}>
                                {' '}· 🔌 {portLabel(p.component_port_id)}
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={td}>
                    {(lun.presented_to || []).length === 0 ? (
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.74rem' }}>
                        No presentado
                      </span>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {lun.presented_to.map(v => (
                          <span key={v.id} style={{ fontSize: '0.74rem' }}>
                            <LinkIcon style={{ width: 11, height: 11, display: 'inline', verticalAlign: -1 }} />
                            {' '}{v.ci_name || `${v.ci_type} ${v.ci_id}`} · <strong>{v.mount_point || v.label || 'volumen'}</strong>
                            {v.size_gb ? ` · ${fmtGb(v.size_gb)}` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={td}>
                    <span style={{
                      fontSize: '0.68rem', borderRadius: 4, padding: '1px 6px',
                      background: lun.status === 'online' ? 'rgba(16,185,129,.16)' : 'rgba(239,68,68,.16)',
                      color: lun.status === 'online' ? '#6ee7b7' : '#fca5a5',
                    }}>
                      {lun.status_label || lun.status}
                    </span>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button className="dv-btn" type="button" title="Editar" onClick={() => openEdit(lun)}>
                      <PencilSquareIcon style={{ width: 14, height: 14 }} />
                    </button>
                    <button className="dv-btn" type="button" title="Eliminar" onClick={() => remove(lun)}
                            style={{ marginLeft: 4 }}>
                      <TrashIcon style={{ width: 14, height: 14 }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
